import ability, { Can } from "../../../../../casl/Can";
import {
  AppAvatar,
  AppBox,
  AppIconButton,
  AppLink,
  AppPaper,
  AppSelect,
  AppTitle,
} from "../../../../../commons/components";
import IconApproval from "../../../../../commons/components/icons/approval";
import IconBin from "../../../../../commons/components/icons/bin";
import IconEdit from "../../../../../commons/components/icons/edit";
import IconProhibited from "../../../../../commons/components/icons/prohibited";
import { ISelectOptions } from "../../../../../commons/components/ui-components/Select";
import { RolePolicyActionEnum } from "../../../../../commons/enums/role-policy-action.enum";
import { TeamStatusEnum } from "../../../../../commons/enums/team-status-enum";
import { ManageFootballSubTeam } from "../../../../../commons/models/football/admin/manage-football-sub-team";
import { ManageFootballTeam } from "../../../../../commons/models/football/admin/manage-football-team";
import { findIndexByCondition } from "../../../../../commons/utilities/array-utils";
import { toTitleCase } from "../../../../../commons/utilities/string-utils";

const SubTeamCard = ({
  teamInfo,
  subTeamInfo,
  onEdit,
  onDelete,
  onStatusChange,
}: {
  teamInfo: ManageFootballTeam;
  subTeamInfo: ManageFootballSubTeam;
  onEdit: () => void;
  onDelete: () => void;
  onStatusChange: (value: string | number) => void;
}) => {
  const canReadSubTeam = ability.can(RolePolicyActionEnum.READ, teamInfo);
  const subteamPath = `/manage/teams/${teamInfo.id}/sub-teams/${subTeamInfo.id}`;

  const teamStatusOptions: ISelectOptions[] = [
    {
      title: "Active",
      icon: <IconApproval color="var(--fg-outline-success)" />,
      value: TeamStatusEnum.Active.toString(),
    },
    {
      title: "Terminated",
      icon: <IconProhibited color="var(--fg-outline-danger)" />,
      value: TeamStatusEnum.Terminated.toString(),
    },
  ];

  const handleLinkClick = (e: MouseEvent) => {
    if (!canReadSubTeam) {
      e.preventDefault();
      e.stopPropagation();
    } else {
      window.scrollTo(0, 0);
    }
  };

  const handleStatusChange = (value: string | number) => {
    onStatusChange(value);
  };

  return (
    <AppPaper padding="xs" shadow="xs">
      <AppBox justifyContent="space-between" alignItems="center">
        <AppLink onClick={handleLinkClick} style={{ pointerEvents: canReadSubTeam ? "auto" : "none" }} to={subteamPath}>
          <AppBox gap="xs" alignItems="center">
            <AppAvatar username={subTeamInfo?.name || ""} src={subTeamInfo?.logo || ""} size="lg" />
            <AppBox flexDirection="column">
              <AppBox alignItems="center" gap="2xs">
                <AppTitle as="h6">{toTitleCase(subTeamInfo?.name)}</AppTitle>
              </AppBox>
            </AppBox>
          </AppBox>
        </AppLink>
        <AppBox gap="xs" justifyContent="end" alignItems="center">
          <AppSelect
            options={teamStatusOptions}
            className="manage-dropdown"
            optionsClassName="manage-dropdown-option"
            disabled={ability.cannot(RolePolicyActionEnum.UPDATE, teamInfo)}
            currentOption={
              teamStatusOptions[
                findIndexByCondition(teamStatusOptions, (option) => option.value === subTeamInfo.status.toString())
              ] ?? teamStatusOptions[1]
            }
            onChange={(option) => handleStatusChange(option.value)}
          />
          <Can I={RolePolicyActionEnum.UPDATE} this={teamInfo}>
            <AppIconButton
              variant="light"
              onClick={() => {
                onEdit();
              }}
              icon={<IconEdit />}
            />
            <AppIconButton
              variant="light"
              color="danger"
              onClick={() => {
                onDelete();
              }}
              icon={<IconBin />}
            />
          </Can>
        </AppBox>
      </AppBox>
    </AppPaper>
  );
};

export default SubTeamCard;
